// Notification types matching the backend
export enum NotificationType {
  PAYMENT_RECEIVED = 'payment_received',
  PAYMENT_OVERDUE = 'payment_overdue',
  PAYMENT_DUE_SOON = 'payment_due_soon',
  CONTRACT_EXPIRING = 'contract_expiring',
  CONTRACT_EXPIRED = 'contract_expired',
  INDEXATION_DUE = 'indexation_due',
  INDEXATION_READY = 'indexation_ready',
  MAINTENANCE_SCHEDULED = 'maintenance_scheduled',
  MAINTENANCE_OVERDUE = 'maintenance_overdue',
  /** Le prestataire a marque sa mission terminee (#371). */
  MAINTENANCE_COMPLETED = 'maintenance_completed',
  DOCUMENT_READY = 'document_ready',
  SYSTEM_UPDATE = 'system_update',
  WELCOME = 'welcome',
  TEST = 'test',
  // Service-provider workflow (Phase 3)
  QUOTE_RECEIVED = 'quote_received',
  QUOTE_ACCEPTED = 'quote_accepted',
  QUOTE_REJECTED = 'quote_rejected',
  INVITATION_ACCEPTED = 'invitation_accepted',
  // Profile completion prompts (Phase 4)
  PROFILE_COMPLETION_PROMPT = 'profile_completion_prompt',
  // Quote follow-up reminders (Phase 4)
  QUOTE_REMINDER = 'quote_reminder',
  /**
   * A cockpit card the backend judged critical — raised immediately instead of
   * waiting for the daily recap. Written by `cockpit/criticalCardAlert.ts`;
   * `actionUrl` deep-links to the card.
   */
  ACTION_CARD_CRITICAL = 'action_card_critical',
  /**
   * Un message entrant a rejoint un dossier qui n'attendait pas de reponse
   * (#371). Sans lui, la proposition de creneau d'un artisan est classee dans
   * un dossier que personne ne rouvrira — pire qu'ignoree, invisible.
   */
  CASE_MESSAGE_FILED = 'case_message_filed',
  /**
   * A support ticket got a reply (#284). Written by
   * `backend/functions/src/support/onSupportMessageCreated.ts`. The body is the
   * ticket SUBJECT, never the reply text — this item is mirrored into a browser
   * push and onto a lock screen. `actionUrl` is `/?support=<threadId>`: the
   * support fil is a tab in the docked panel, not a route of its own.
   */
  SUPPORT_REPLY = 'support_reply',
  /**
   * A document was saved but its email could not be sent. Written by
   * `backend/functions/src/email/sendFailureNotification.ts`; `actionUrl`
   * points at the mail outbox, where the draft can be resent.
   */
  EMAIL_SEND_FAILED = 'email_send_failed',
  /**
   * The 7-day payment grace period expired and the property was hidden.
   * Written by `backend/functions/src/scheduledBillingChecks.ts`.
   */
  BILLING_GRACE_PERIOD_EXPIRED = 'billing_grace_period_expired',
  /**
   * Stripe could not charge the card; the subscription is suspended. Written
   * by the `invoice.payment_failed` webhook in
   * `backend/functions/src/stripe/index.ts`.
   */
  BILLING_PAYMENT_FAILED = 'billing_payment_failed'
}

// Notification categories for grouping
export enum NotificationCategory {
  FINANCIAL = 'financial',
  CONTRACTS = 'contracts',
  MAINTENANCE = 'maintenance',
  DOCUMENTS = 'documents',
  SYSTEM = 'system',
  REMINDERS = 'reminders',
  PROVIDERS = 'providers'
}

// Notification priority levels
export enum NotificationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent'
}

// Main notification interface
export interface Notification {
  notificationId: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  data?: Record<string, unknown>; // Context-specific data (contractId, paymentId, etc.)
  read: boolean;
  createdAt: Date;
  readAt?: Date;
  expiresAt?: Date;
  priority: NotificationPriority;
  actionUrl?: string; // Optional URL for navigation
  actionLabel?: string; // Optional action button label
  icon?: string; // Optional icon identifier
}

// Notification summary interface
export interface NotificationSummary {
  totalCount: number;
  unreadCount: number;
  categories: Record<NotificationCategory, number>;
}

// API response interfaces
export interface AddNotificationRequest {
  // SECURITY NOTE: userId is no longer accepted by backend for security reasons
  // Backend automatically uses authenticated user's ID
  // Including userId in request will be ignored
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  priority?: NotificationPriority;
  actionUrl?: string;
  actionLabel?: string;
  icon?: string;
  expiresInDays?: number;
}

export interface NotificationResponse {
  success: boolean;
  message: string;
  notificationId?: string;
  totalCount?: number;
  unreadCount?: number;
}

export interface GetNotificationsRequest {
  limit?: number;
  offset?: number;
  category?: NotificationCategory;
  unreadOnly?: boolean;
}

export interface GetNotificationsResponse {
  success: boolean;
  notifications: Notification[];
  totalCount: number;
  unreadCount: number;
  filteredCount: number;
  hasMore: boolean;
}

// UI-specific interfaces
export interface NotificationGrouping {
  today: Notification[];
  yesterday: Notification[];
  thisWeek: Notification[];
  older: Notification[];
}

export interface NotificationFilter {
  category?: NotificationCategory;
  priority?: NotificationPriority;
  unreadOnly?: boolean;
  search?: string;
} 